/**
 * The blocks on offer.
 *
 * Each tile is both draggable and clickable. Dragging is the obvious gesture
 * and clicking is the one that works: on a trackpad, dragging a tile across a
 * scrolling canvas to a target that has to be held open is a fiddly move, so a
 * click appends the block to the end of the letter and selects it.
 */
import type { DragEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../i18n/I18nProvider";
import { BLOCK_CATALOG, DRAG_NEW } from "./catalog";
import { useBuilder } from "./store";
import type { BlockType } from "./types";

export function Palette() {
  const t = useT();
  const { dispatch, disabled } = useBuilder();

  function onDragStart(e: DragEvent, type: BlockType) {
    e.dataTransfer.setData(DRAG_NEW, type);
    e.dataTransfer.effectAllowed = "copy";
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] leading-snug text-muted-foreground">{t("marketing.builder.paletteHint")}</p>
      <div className="grid grid-cols-2 gap-1.5">
        {BLOCK_CATALOG.map((meta) => {
          const Icon = meta.icon;
          return (
            <button
              key={meta.type}
              type="button"
              draggable={!disabled}
              disabled={disabled}
              onDragStart={(e) => onDragStart(e, meta.type)}
              onClick={() => dispatch({ kind: "add", type: meta.type, index: -1 })}
              title={t(`marketing.builder.blockHint.${meta.key}`)}
              className={cn(
                "group flex cursor-grab flex-col items-start gap-1.5 rounded-lg border border-border bg-card/60 px-2.5 py-2 text-left transition-colors",
                "hover:border-primary/40 hover:bg-accent/50 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <span className="flex w-full items-center gap-1.5">
                <Icon className="size-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                  {t(`marketing.builder.block.${meta.key}`)}
                </span>
                {meta.caveat && (
                  <AlertTriangle className="size-3 shrink-0 text-amber-500" aria-hidden />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
